import { useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import { useCallback, useMemo, useRef } from 'react';

import Api from 'Api/Api';
import { url } from '@api-config';
import { IActiveIngestion } from '../../redux/ingestion.interface';
import { IngestionLogCountsQueryKey } from './useIngestionLogCounts.query';
import { ingestionLogsKeys } from './useIngestionLogSummary.query';
import { useSolutionList } from '../../context/Solution/hooks/useSolutionList';
import { useAppSelector } from '@redux/store';
import { useFetchLogDetails } from '../IngestionLayout/hooks/useFetchLogDetails';

export const dataSourceIngestionKeys = {
  invalidationKey: ['dataSourceIngestionStatus'],
  queryKey: (id?: number) => ['dataSourceIngestionStatus', id],
};

/**
 * Iterate the current data and create a Set of ingestion id's
 * @param data
 * @returns Set<string> of current ingestion id's
 */
const getCurrentIngestionIds = (data: IActiveIngestion[]): Set<string> => {
  return new Set(
    data.reduce((acc: string[], item) => {
      const activeTables = Object.keys(item?.tables || {});

      if (activeTables.length > 0) {
        return [...acc, ...activeTables.map((tableName) => item.tables?.[tableName]?.ingestionId)];
      } else {
        return [...acc, item.ingestionId];
      }
    }, [])
  );
};

/**
 * Iterate the previous active ingestions to pick out the ingestion id's
 * that are no longer in the array of current active ingestions
 * If they're no longer in the current active array, it means they have completed
 * @param previousData
 * @param currentDataIngestionIds
 * @returns string[] of ingestions that completed
 */
const getNewlyCompletedIngestionIds = (
  previousData: IActiveIngestion[],
  currentDataIngestionIds: Set<string>
): string[] => {
  let newlyCompletedIds: string[] = [];

  previousData.forEach((oldItem) => {
    const activeTables = Object.keys(oldItem?.tables || {});

    if (activeTables.length > 0) {
      activeTables.forEach((tableName) => {
        const ingestionId = oldItem?.tables?.[tableName]?.ingestionId;
        if (!currentDataIngestionIds.has(ingestionId)) {
          newlyCompletedIds.push(ingestionId);
        }
      });
    } else {
      if (!currentDataIngestionIds.has(oldItem.ingestionId)) {
        newlyCompletedIds.push(oldItem.ingestionId);
      }
    }
  });

  return newlyCompletedIds;
};

/**
 * Hook used to fetch active ingestions, when an ingestion is triggered to run (or stop)
 * After triggering an ingestion, the API request will refetch every 5 seconds to report status
 * Current data is then compared with previous data
 * When there is a change, other queries get invalidated, which trigger fetching the final status
 * (finished, failed, etc)
 * @param param0
 * @returns QueryResult & isRunning status
 */
export const useDSIngestionStatus = (): UseQueryResult<IActiveIngestion[], Error> & {
  isRunning: boolean;
} => {
  const previousDataRef = useRef<IActiveIngestion[] | null>(null);

  const queryClient = useQueryClient();

  const { solution } = useSolutionList();
  const id = useMemo(() => solution?.id, [solution]);

  const selectedLogId = useAppSelector((state) => state.ingestion.selectedLogId);

  const { fetchDetails } = useFetchLogDetails();

  /**
   * Compare new data & previous data
   * If the status has changed, invalidate queries to re-fetch the final ingestion status
   * (failed, success, etc)
   */
  const handleChangeInActiveIngestions = useCallback(
    (currentData: IActiveIngestion[]) => {
      const previousData = previousDataRef.current;
      let newlyCompletedIds: string[] = [];

      if (previousData) {
        const currentDataIngestionIds = getCurrentIngestionIds(currentData);
        newlyCompletedIds = getNewlyCompletedIngestionIds(previousData, currentDataIngestionIds);
      }

      if (newlyCompletedIds.length > 0) {
        // This is responsible for refreshing the logs whenever an ingestion is completed
        queryClient.invalidateQueries({ queryKey: ingestionLogsKeys.all });
        queryClient.invalidateQueries({ queryKey: IngestionLogCountsQueryKey });

        // This call happens if you had the ingestion logs open, so the status is updated
        if (selectedLogId !== null && newlyCompletedIds.includes(selectedLogId)) {
          fetchDetails(selectedLogId);
        }
      }
      // update the ref with the latest ingestion data
      previousDataRef.current = currentData;
    },
    [previousDataRef, queryClient, selectedLogId, fetchDetails]
  );

  const query = useQuery<IActiveIngestion[], Error>({
    queryKey: dataSourceIngestionKeys.queryKey(id),
    enabled: !!id && id > -1,
    queryFn: async () => {
      const data = await Api.get(`${url.ingestionStatusByDataSource}/${id}`);
      handleChangeInActiveIngestions(data);
      return data;
    },
    refetchInterval: ({ state }) => ((state.data?.length ?? 0) > 0 ? 5000 : false),
    staleTime: Infinity,
  });

  return {
    ...query,
    isRunning: query.data ? query.data.length > 0 : false,
  };
};
